import React, {useCallback, useState} from 'react'
import {useHistory} from 'react-router-dom'
import HouseholdLeaveModal from './HouseholdLeaveModal'
import {IApplicationFormData, IWizardReviewSummary} from '../../../models/application-form'
import {
  getApplicantName,
  getPrimaryRoleLabelWithHoushold,
  isDeclinedInviteStatus,
  normalizeMobile,
} from '../renterHomeUtils'
import {HouseholdPersonRow} from './HouseholdPersonRow'
import CoApplicantInviteModal from '../../common/CoApplicantInviteModal'
import {
  deleteCoApplicantInvite,
  resendCoApplicantInvite,
} from '../../../api'
import {Modal} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {useRetryCountdown} from '../../common/useRetryCountdown'

type HouseholdTabProps = {
  household: IWizardReviewSummary | null
  draft: IApplicationFormData | null
  isPrimaryApplicant: boolean
  sessionId?: string
  onHouseholdChanged: () => void | Promise<void>
}

export type HouseholdPerson = {
  applicationId?: number
  key: string
  inviteId?: string
  name: string
  mobileNumber?: string
  email?: string
  role?: string
  status?: string
  isVerified?: boolean
  isCardComplete?: boolean
  isPendingInvite?: boolean
  membershipStatus?: string
  householdMemberId?: string
  dooraCardId?: number
  mobileVerifiedAtUtc?: string
  cardCompletedAtUtc?: string
  invitedAtUtc?: string
  inviteExpiresAtUtc?: string
  otpExpiresAt?: string
  declinedAtUtc?: string
  declineReason?: string
  relationship?: string
  canResend?: boolean
  inviteType?: 'co-applicant'
  missingRequiredFields?: string[]
}

const HouseholdTab: React.FC<HouseholdTabProps> = ({
  household,
  draft,
  isPrimaryApplicant,
  sessionId,
  onHouseholdChanged,
}) => {
  const [showInviteModal, setShowInviteModal] = useState(false)
  const resendRetry = useRetryCountdown()
  const [inviteMessage, setInviteMessage] = useState<string | null>(null)
  const [openMenuPersonKey, setOpenMenuPersonKey] = useState<string | null>(null)
  const [resendingInviteId, setResendingInviteId] = useState<string | null>(null)
  const [removingInviteId, setRemovingInviteId] = useState<string | null>(null)
  const [removedInviteIds, setRemovedInviteIds] = useState<string[]>([])
  const [inviteToRemove, setInviteToRemove] = useState<HouseholdPerson | null>(null)
  const [memberToRemove, setMemberToRemove] = useState<HouseholdPerson | null>(null)
  const history = useHistory()
  const leaveCompleted = useCallback(async () => {
    setMemberToRemove(null)
    if (isPrimaryApplicant) await onHouseholdChanged()
    else history.replace('/application-landing')
  }, [isPrimaryApplicant, onHouseholdChanged, history])

  const applicationType = household?.applicationType || draft?.applicationType || 'Individual'
  const isIndividualApplication = applicationType === 'Individual'
  const activeMembers = getActiveHouseholdPeople(household, draft)
  const pendingInvites = isIndividualApplication
    ? []
    : getPendingHouseholdInvites(household, draft).filter(
        (invite) => !invite.inviteId || !removedInviteIds.includes(invite.inviteId)
      )
  const declinedInvites = pendingInvites.filter((person) =>
    isDeclinedInviteStatus(person.status, person.declinedAtUtc, person.declineReason)
  )
  const peopleCount = activeMembers.length + pendingInvites.length

  const primaryPerson: HouseholdPerson = activeMembers[0] ?? {
    key: 'primary',
    name: getApplicantName(household, draft),
    mobileNumber: household?.mobileNumber || draft?.mobileNumber,
    email: household?.email || draft?.email,
    role: getPrimaryRoleLabelWithHoushold(household, draft),
    status: 'verified',
    isVerified: true,
    isCardComplete: true,
    applicationId: household?.applicationId || draft?.applicationId,
  }
  const secondaryActiveMembers = activeMembers.slice(1)

  const currentMemberKey = isPrimaryApplicant
    ? undefined
    : [...activeMembers, ...pendingInvites].find((person) => isCurrentApplicant(person, draft))?.key

  const resendInvite = async (person: HouseholdPerson) => {
    const activeSessionId = (sessionId ?? draft?.sessionId)?.trim()
    const inviteId = person.inviteId?.trim()
    if (!activeSessionId || !inviteId) {
      setInviteMessage('Invite reference is missing. Refresh and try again.')
      return
    }
    if (resendingInviteId || removingInviteId || resendRetry.seconds > 0) return

    setOpenMenuPersonKey(null)
    setInviteMessage(null)
    setResendingInviteId(inviteId)
    try {
      const response = await resendCoApplicantInvite({
        sessionId: activeSessionId,
        inviteId,
      })
      if (!response.success) {
        resendRetry.start(response.retryAfterSeconds)
        toast.error(response.message ?? 'Unable to resend invite right now.', {
          autoClose: 2000,
        })

        setInviteMessage(response.message ?? 'Unable to resend invite right now.')
        return
      }

      toast.success('Invite has been sent.', {icon: false})
      await onHouseholdChanged()
    } catch {
      setInviteMessage('Unable to resend invite right now.')
    } finally {
      setResendingInviteId(null)
    }
  }

  const removeInvite = async (person: HouseholdPerson) => {
    const activeSessionId = (sessionId ?? draft?.sessionId)?.trim()
    const inviteId = person.inviteId?.trim()
    if (!activeSessionId || !inviteId) {
      setInviteMessage('Invite reference is missing. Refresh and try again.')
      return
    }
    if (removingInviteId || resendingInviteId) return

    setOpenMenuPersonKey(null)
    setInviteMessage(null)
    setRemovingInviteId(inviteId)
    try {
      const response = await deleteCoApplicantInvite({
        sessionId: activeSessionId,
        inviteId,
      })
      if (!response.success) {
        setInviteMessage(response.message ?? 'Unable to remove invite right now.')
        return
      }

      setRemovedInviteIds((current) => [...current, inviteId])
      setInviteToRemove(null)
      setInviteMessage(response.message ?? 'Invite removed.')
      await onHouseholdChanged()
    } catch {
      setInviteMessage('Unable to remove invite right now.')
    } finally {
      setRemovingInviteId(null)
    }
  }

  return (
    <section className='renter-home-section'>
      <div className='renter-home-section-header'>
        <div>
          <h2>Your Household</h2>
          <p>
            {peopleCount} {peopleCount === 1 ? 'person' : 'people'} on this application
          </p>
        </div>
      </div>

      <div className='renter-household-list'>
        <HouseholdPersonRow
          person={primaryPerson}
          isPrimary={true}
          isCurrentUser={isPrimaryApplicant && isCurrentApplicant(primaryPerson, draft)}
          isMenuOpen={openMenuPersonKey === primaryPerson.key}
          onMenuToggle={() =>
            setOpenMenuPersonKey((current) =>
              current === primaryPerson.key ? null : primaryPerson.key
            )
          }
          onMenuClose={() => setOpenMenuPersonKey(null)}
        />

        {secondaryActiveMembers.map((member) => (
          <HouseholdPersonRow
            key={member.key}
            person={member}
            isCurrentUser={currentMemberKey === member.key}
            isMenuOpen={openMenuPersonKey === member.key}
            onMenuToggle={() =>
              setOpenMenuPersonKey((current) => (current === member.key ? null : member.key))
            }
            onMenuClose={() => setOpenMenuPersonKey(null)}
            onRemoveInvite={member.householdMemberId && (isPrimaryApplicant || currentMemberKey === member.key)
              ? () => { setOpenMenuPersonKey(null); setMemberToRemove(member) } : undefined}
            removeLabel={isPrimaryApplicant ? 'Remove member' : 'Leave this household'}
          />
        ))}
      </div>

      {memberToRemove?.householdMemberId && (sessionId || draft?.sessionId) && (
        <HouseholdLeaveModal sessionId={(sessionId || draft?.sessionId)!}
          householdMemberId={memberToRemove.householdMemberId} name={memberToRemove.name}
          mobileVerifiedAtUtc={memberToRemove.mobileVerifiedAtUtc}
          isSelf={!isPrimaryApplicant} onHide={() => setMemberToRemove(null)} onCompleted={leaveCompleted} />
      )}

      {isPrimaryApplicant && declinedInvites.length > 0 && (
        <div className='renter-household-declined-note' role='status'>
          <i className='bi bi-exclamation-triangle' aria-hidden='true'></i>
          <span>
            An invitee selected "This isn't me". The contact details may be wrong. Remove the
            declined invite, then add the correct person with the right number.
          </span>
        </div>
      )}

      {pendingInvites.length > 0 && (
        <>
          <div className='renter-section-kicker mt-5'>INVITES</div>
          <div className='renter-household-list'>
            {pendingInvites.map((pendingInvite) => (
              <HouseholdPersonRow
                key={pendingInvite.key}
                person={pendingInvite}
                isCurrentUser={currentMemberKey === pendingInvite.key}
                isMenuOpen={openMenuPersonKey === pendingInvite.key}
                onMenuToggle={() =>
                  setOpenMenuPersonKey((current) =>
                    current === pendingInvite.key ? null : pendingInvite.key
                  )
                }
                onMenuClose={() => setOpenMenuPersonKey(null)}
                onResendInvite={
                  isPrimaryApplicant && pendingInvite.inviteId
                    ? () => void resendInvite(pendingInvite)
                    : undefined
                }
                onRemoveInvite={
                  isPrimaryApplicant && pendingInvite.inviteId
                    ? () => {
                        setOpenMenuPersonKey(null)
                        setInviteMessage(null)
                        setInviteToRemove(pendingInvite)
                      }
                    : undefined
                }
                isResending={resendingInviteId === pendingInvite.inviteId}
                resendRetrySeconds={resendRetry.seconds}
                isRemoving={removingInviteId === pendingInvite.inviteId}
              />
            ))}
          </div>
        </>
      )}

      {!isIndividualApplication &&
        isPrimaryApplicant &&
        pendingInvites.length === 0 &&
        activeMembers.length <= 1 && (
          <div className='renter-empty-state'>
            No co-applicants added yet.
            {isPrimaryApplicant
              ? ' You can add another person when this application needs one.'
              : ''}
          </div>
        )}

      {!isIndividualApplication && isPrimaryApplicant && (
        <div className='renter-household-note'>
          <i className='bi bi-info-circle' aria-hidden='true'></i>
          Invite links are valid for 7 days. Pending applicants will receive a reminder after 3
          days. You can resend when their code or invite expires, or remove them anytime.
        </div>
      )}

      {isPrimaryApplicant && !isIndividualApplication && (
        <>
          <button
            type='button'
            className='renter-add-person-btn'
            onClick={() => {
              setInviteMessage(null)
              setShowInviteModal(true)
            }}
          >
            <i className='bi bi-plus-lg' aria-hidden='true'></i>
            Add another person
          </button>

          {inviteMessage && (
            <div
              className={`small mt-3 ${
                /unable|required|expired|missing|right now/i.test(inviteMessage)
                  ? 'text-danger'
                  : 'text-success'
              }`}
              role='status'
            >
              {inviteMessage}
            </div>
          )}

          <CoApplicantInviteModal
            isOpen={showInviteModal}
            sessionId={sessionId ?? draft?.sessionId}
            ownerMobileNumber={household?.mobileNumber || draft?.mobileNumber}
            existingCoApplicants={[...secondaryActiveMembers, ...pendingInvites].map(
              (applicant) => ({
                mobileNumber: applicant.mobileNumber,
                status: applicant.status,
              })
            )}
            onClose={() => setShowInviteModal(false)}
            onInvited={async () => {
              setInviteMessage(null)
              toast.success('Invite has been sent.', {icon: false})
              await onHouseholdChanged()
            }}
          />
        </>
      )}

      <Modal
        contentClassName='renter-household-modal'
        show={Boolean(inviteToRemove)}
        onHide={() => {
          if (!removingInviteId) {
            setInviteToRemove(null)
          }
        }}
        backdrop={removingInviteId ? 'static' : true}
        keyboard={!removingInviteId}
        centered
      >
        <Modal.Body>
          <div className='delete'>
            <div className='delete-icon'>
              <svg
                width='22'
                height='22'
                viewBox='0 0 20 20'
                fill='none'
                stroke='#EF4444'
                strokeWidth='1.6'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path d='M4 5.5h12M8 5.5V4a1.5 1.5 0 011.5-1.5h1A1.5 1.5 0 0112 4v1.5M5.5 5.5l.8 10A1.5 1.5 0 007.8 17h4.4a1.5 1.5 0 001.5-1.5l.8-10'></path>
              </svg>
            </div>
            <h2>
              Remove{' '}
              {inviteToRemove?.name || 'this co-applicant'}
              ?
            </h2>
            <p>They won’t be part of this application. Their invite link will stop working.</p>
            <div className='footer'>
              <button
                type='button'
                className='cancel'
                disabled={Boolean(removingInviteId)}
                onClick={() => setInviteToRemove(null)}
              >
                Cancel
              </button>
              <button
                type='button'
                className='remove'
                disabled={Boolean(removingInviteId) || !inviteToRemove}
                onClick={() => {
                  if (inviteToRemove) {
                    void removeInvite(inviteToRemove)
                  }
                }}
              >
                <svg
                  width='14'
                  height='14'
                  viewBox='0 0 20 20'
                  fill='none'
                  stroke='#fff'
                  strokeWidth='2.4'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <path d='M4 5.5h12M8 5.5V4a1.5 1.5 0 011.5-1.5h1A1.5 1.5 0 0112 4v1.5M5.5 5.5l.8 10A1.5 1.5 0 007.8 17h4.4a1.5 1.5 0 001.5-1.5l.8-10'></path>
                </svg>{' '}
                {removingInviteId ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>

          {inviteMessage && /unable|required|expired|missing|right now/i.test(inviteMessage) && (
            <div className='text-danger small mt-3' role='alert'>
              {inviteMessage}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </section>
  )
}

const getPrimaryPerson = (
  summary: IWizardReviewSummary | null,
  draft: IApplicationFormData | null
): HouseholdPerson => ({
  key: 'primary',
  name: getApplicantName(summary, draft),
  mobileNumber: summary?.mobileNumber || draft?.mobileNumber,
  email: summary?.email || draft?.email,
  role: getPrimaryRoleLabelWithHoushold(summary, draft),
  status: 'verified',
  isVerified: true,
  isCardComplete: true,
  applicationId: summary?.applicationId || draft?.applicationId,
})

const isPrimaryRole = (role?: string): boolean => role?.trim().toLowerCase() === 'primary'

const isCompletedOrVerifiedStatus = (person: HouseholdPerson): boolean => {
  const status = person.status?.trim().toLowerCase()
  return (
    person.isCardComplete === true ||
    person.isVerified === true ||
    status === 'accepted' ||
    status === 'verified' ||
    status === 'complete' ||
    status === 'completed' ||
    status === 'submitted'
  )
}

const getActiveHouseholdPeople = (
  summary: IWizardReviewSummary | null,
  draft: IApplicationFormData | null
): HouseholdPerson[] => {
  const targetMembers = summary?.activeMembers ?? []
  if (targetMembers.length > 0) {
    return targetMembers
      .map((member, index) => ({
        key:
          member.householdMemberId ??
          member.mobileNumber ??
          member.fullName ??
          `household-member-${index}`,
        householdMemberId: member.householdMemberId,
        dooraCardId: member.dooraCardId,
        applicationId: member.applicationId,
        name:
          member.fullName || (index === 0 ? getApplicantName(summary, draft) : 'Household member'),
        mobileNumber: member.mobileNumber,
        email: member.email,
        role:
          member.role ||
          (index === 0 ? getPrimaryRoleLabelWithHoushold(summary, draft) : 'Co-applicant'),
        status: member.membershipStatus || 'active',
        membershipStatus: member.membershipStatus,
        isVerified: member.isMobileVerified,
        mobileVerifiedAtUtc: member.mobileVerifiedAtUtc,
        isCardComplete: member.isCardComplete,
        cardCompletedAtUtc: member.cardCompletedAtUtc,
        missingRequiredFields: member.missingRequiredFields,
      }))
      .sort((left, right) => Number(isPrimaryRole(right.role)) - Number(isPrimaryRole(left.role)))
  }

  const primaryPerson = getPrimaryPerson(summary, draft)
  const activeCoApplicants = getLegacyCoApplicantPeople(summary, draft).filter(
    isCompletedOrVerifiedStatus
  )

  return [primaryPerson, ...activeCoApplicants]
}

const getPendingHouseholdInvites = (
  summary: IWizardReviewSummary | null,
  draft: IApplicationFormData | null
): HouseholdPerson[] => {
  const targetInvites = summary?.pendingInvites ?? []
  if (targetInvites.length > 0) {
    return targetInvites.map((invite, index) => ({
      key:
        invite.inviteId ??
        invite.householdInviteId ??
        invite.mobileNumber ??
        invite.fullName ??
        `pending-invite-${index}`,
      inviteId: invite.inviteId ?? invite.householdInviteId,
      name: invite.fullName || 'Co-applicant',
      mobileNumber: invite.mobileNumber,
      email: invite.email,
      role: invite.role || 'Co-applicant',
      status: invite.status || invite.inviteStatus || 'pending',
      isVerified: invite.isMobileVerified,
      isCardComplete: false,
      isPendingInvite: true,
      invitedAtUtc: invite.invitedAtUtc,
      inviteExpiresAtUtc: invite.inviteExpiresAtUtc,
      otpExpiresAt: invite.otpExpiresAt,
      declinedAtUtc: invite.declinedAtUtc,
      declineReason: invite.declineReason,
      relationship: invite.relationship,
      canResend: invite.canResend,
      inviteType: 'co-applicant',
      missingRequiredFields: invite.missingRequiredFields,
    }))
  }

  return getLegacyCoApplicantPeople(summary, draft).filter(
    (person) => !isCompletedOrVerifiedStatus(person)
  )
}

const getLegacyCoApplicantPeople = (
  summary: IWizardReviewSummary | null,
  draft: IApplicationFormData | null
): HouseholdPerson[] => {
  const summaryItems = summary?.coApplicantStatuses ?? []

  if (summaryItems.length > 0) {
    return summaryItems.map((item) => ({
      key: item.inviteId ?? item.mobileNumber ?? item.fullName ?? 'co-applicant',
      inviteId: item.inviteId,
      name: item.fullName || 'Co-applicant',
      mobileNumber: item.mobileNumber,
      role: item.role || 'Co-applicant',
      status: item.isCompleted || item.isMobileVerified ? 'accepted' : item.status,
      isVerified: item.isCompleted || item.isMobileVerified,
      isCardComplete: item.isCompleted,
      missingRequiredFields: item.missingRequiredFields,
      applicationId: item.coApplicantApplicationId,
      invitedAtUtc: item.invitedAtUtc?.toString(),
      inviteExpiresAtUtc: item.inviteExpiresAtUtc?.toString(),
      otpExpiresAt: item.otpExpiresAt?.toString(),
      declinedAtUtc: item.declinedAtUtc?.toString(),
      declineReason: item.declineReason,
      inviteType: 'co-applicant',
    }))
  }

  return (draft?.coApplicants ?? []).map((item, index) => ({
    key: item.inviteId ?? item.mobileNumber ?? `co-applicant-${index}`,
    inviteId: item.inviteId,
    name: item.fullName || 'Co-applicant',
    mobileNumber: item.mobileNumber,
    email: item.email,
    role: item.role || 'Co-applicant',
    status: item.isMobileVerified ? 'accepted' : item.status,
    isVerified: item.isMobileVerified,
    isCardComplete: item.status?.trim().toLowerCase() === 'completed',
    isPendingInvite: item.isMobileVerified !== true,
    invitedAtUtc: item.invitedAtUtc,
    declinedAtUtc: item.declinedAtUtc,
    declineReason: item.declineReason,
    inviteType: 'co-applicant',
  }))
}

const isCurrentApplicant = (
  person: HouseholdPerson,
  draft: IApplicationFormData | null
): boolean => {
  if (!draft) {
    return false
  }

  if (
    draft.applicationId !== undefined &&
    person.applicationId !== undefined &&
    draft.applicationId === person.applicationId
  ) {
    return true
  }

  const draftMobileNumber = normalizeMobile(draft.mobileNumber ?? '')
  const personMobileNumber = normalizeMobile(person.mobileNumber ?? '')

  return Boolean(
    draftMobileNumber && personMobileNumber && draftMobileNumber === personMobileNumber
  )
}

export default HouseholdTab
